'use strict';
import reduce from 'lodash/reduce';
import {SyncStatus} from '../../_libs/redux-observable-list';
import {epicErrorHandler} from '../../core-utilities/rx-utils';
import {updatePosition} from '../../settings/actions';
import {pauseTimer, startTimer, stopTimer} from '../actions';
import {getActiveTimers} from '../reducers/state';
import {findAllByTarget, getListId} from '../reducers/activeTimersReducers';
import {extractLocation} from '../utils/locationUtils';

export class PreviewTimerLogEpics {

  /**
   * @param {ReduxObservableListActions} actions
   * @param {Object} options
   * @param {bunyan} logger
   */
  constructor(actions, options, logger) {

    /** @type {ReduxObservableListActions} */
    this.actions = actions;

    this.targetCollection = options.targetCollection;
    this.getLogState = options.getState;

    this.logger = logger;

    this.previewLogEpic = this.previewLogEpic.bind(this);
    this.unpreviewLogEpic = this.unpreviewLogEpic.bind(this);

    this.epics = [
      this.previewLogEpic,
      this.unpreviewLogEpic,
    ];
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {NgStore} store
   * @return {Observable<ReduxAction>}
   */
  previewLogEpic(action$, store) {
    return action$.ofType(this.actions.initList.TYPE)
      .map(action => action.payload.parentId)
      .switchMap(parentId =>
        action$.ofType(startTimer.TYPE, pauseTimer.TYPE, stopTimer.TYPE, updatePosition.TYPE)
          .throttleTime(2500, void 0, {leading: true, trailing: true})
          .map(() => {
            const rootState = store.getState();
            const activeTimers = getActiveTimers(rootState).list;
            const existingItems = findAllByTarget(this.targetCollection, parentId, activeTimers);

            if (existingItems.length === 0) {
              return this.removePendingItems(rootState);
            } else {
              const logState = this.getLogState(rootState);
              // Start with the items to delete, if they are no longer a preview
              const payload = PreviewTimerLogEpics.getPreviewIds(logState.list);

              existingItems.forEach(existingItem => {
                // convert the intervals without an id to preview log items
                existingItem.intervals.forEach(interval => {
                  if (!interval.id) {
                    const previewId = `preview-${interval.start}`;
                    payload[previewId] = {
                      id: previewId,
                      requestId: existingItem.id || existingItem.requestId,
                      isPreview: true,
                      start: interval.start,
                      end: interval.end,
                      locCap: existingItem.locCap,
                      loc: extractLocation(existingItem.loc, interval.start, interval.end),
                      byName: 'Me',
                    };
                  }
                });
              });

              return Object.keys(payload).length > 0 ? this.actions.loadSuccess({list: payload}) : null;
            }
          })
          .filter(action => action !== null)
          .takeUntil(action$.ofType(this.actions.destroyList.TYPE))
      )
      .catch(epicErrorHandler(this.logger, 'previewLogEpic'));
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {NgStore} store
   * @return {Observable<ReduxAction>}
   */
  unpreviewLogEpic(action$, store) {
    return action$.ofType(this.actions.initList.TYPE)
      .map(action => action.payload.parentId)
      .switchMap(parentId =>
        // wait for the first successful page load before watching list items
        action$.ofType(this.actions.updateSyncStatus.TYPE)
          .filter(action => {
            if (action.payload.syncStatus === SyncStatus.SUCCESS
              || action.payload.syncStatus === SyncStatus.UNRECOVERABLE) {
              if (action.payload.requestId || action.payload.id)
                return true;

              // this may not be needed at all
              const activeTimers = getActiveTimers(store.getState()).list;
              const itemId = getListId(activeTimers, action.payload.requestId, action.payload.id);
              const item = itemId && activeTimers[itemId];
              return item && item.target.id === parentId && item.target.collection === this.targetCollection;
            } else {
              return false;
            }
          })
          .map(action => this.removePendingItems(store.getState(), action.payload.requestId, action.payload.id))
          .filter(action => action !== null)
          .takeUntil(action$.ofType(this.actions.destroyList.TYPE))
      )
      .catch(epicErrorHandler(this.logger, 'previewLogEpic'));
  }

  removePendingItems(rootState, requestId, id) {
    const logState = this.getLogState(rootState);
    const payload = PreviewTimerLogEpics.getPreviewIds(logState.list, requestId, id);
    return Object.keys(payload).length > 0 ? this.actions.loadSuccess({list: payload}) : null;
  }

  static getPreviewIds(logList, requestId, itemId) {
    return reduce(logList, (acc, item, id) => {
      if (item.isPreview && ((!requestId && !itemId) || (item.requestId === requestId || item.requestId === itemId)))
        acc[id] = null;

      return acc;
    }, {});
  }
}



// WEBPACK FOOTER //
// ./src/js/timers/epics/previewTimerLog.js